import React, { useEffect, useRef, useState } from 'react';
import { Profile } from '~/types/models';
import { useProfiles } from '~/App/Api/Profile';
import { Input, InputProps } from '@vimily/bonjoro-ui-components-v2/Input';
import Loading from './Loading';
import NoResult from './NoResult';
import {
  Dropdown,
  DropdownAlignment,
} from '@vimily/bonjoro-ui-components-v2/Dropdown';
import SearchResults from './SearchResults';
import cx from 'classnames';

export interface ProfileSearchBoxProps extends InputProps {
  value?: string;
  className?: string;
  onInputChange?: (value: string) => void;
  onProfileSelect?: (profile: Profile) => void;
}

export default function ProfileSearchBox({
  value = '',
  className,
  onInputChange,
  onProfileSelect,
  ...inputProps
}: ProfileSearchBoxProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [activeDropdown, setActiveDropdown] = useState(false);

  const [query, setQuery] = useState(value);

  //used to debounce the profile search
  const [searchQuery, setSearchQuery] = useState(value);

  const [selectedContact, setSelectedContact] = useState<Profile>();

  const { data: profiles = [], isLoading } = useProfiles({
    required: [],
    search: searchQuery,
  });

  const onInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    onInputChange?.(e.target.value);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(query);
    }, 200);

    return () => clearTimeout(timeout);
  }, [query]);

  const clearSelectedContact = () => setSelectedContact(undefined);

  const onContactSelected = (contact: Profile) => {
    setSelectedContact(contact);
    onProfileSelect?.(contact);
    setActiveDropdown(false);
  };

  useEffect(() => {
    clearSelectedContact();
  }, [query]);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (inputRef?.current === document.activeElement) {
      setActiveDropdown(true);
    }
  }, [inputRef?.current]);

  const onEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      setActiveDropdown(false);
    }
  };

  const noResult = profiles.length === 0 && !isLoading;

  return (
    <Dropdown
      body={
        <Input
          {...inputProps}
          className={cx(className, 'cursor-pointer')}
          inputRef={inputRef}
          value={value}
          onChange={onInputChangeHandler}
          onKeyDown={onEmailKeyDown}
          onClick={() => setActiveDropdown(true)}
        />
      }
      active={activeDropdown}
      containerAlignment={DropdownAlignment.Start}
      containerClassName='drop-shadow-md p-0 !w-auto !h-auto z-99'
    >
      <div
        className={cx(
          [
            'overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-neutral-200',
            'scrollbar-thumb-neutral-400 scrollbar-thumb-rounded-full',
          ],
          {
            '!h-[150px]': !noResult,
          }
        )}
      >
        {isLoading ? (
          <Loading />
        ) : (
          <SearchResults
            selectedContact={selectedContact}
            contacts={profiles}
            onContactSelected={onContactSelected}
          />
        )}
        {noResult && <NoResult query={query} />}
      </div>
    </Dropdown>
  );
}
